import React, { useEffect } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Brain, Briefcase, GraduationCap, Sparkles } from "lucide-react";
import ChatInterface from "@/components/ai-tutor/ChatInterface";
import { useSupabaseAuth } from "@/hooks/auth";
import { logActivity } from "@/utils/activityLogger";

const AITutorContent = () => {
  const { user } = useSupabaseAuth();

  // Log AI Tutor access
  useEffect(() => {
    if (!user) {
      console.log('🔍 AI Tutor Access Logger: Skip (no user)');
      return;
    }
    
    const logAITutorAccess = async () => {
      console.log('📊 Logging AI Tutor access');
      
      try {
        await logActivity({
          userId: user.id,
          courseId: 'ai-tutor',
          activityType: 'ai_tutor_access',
          metadata: {
            page: 'ai-tutor',
            timestamp: new Date().toISOString()
          }
        });
        
        console.log('✅ AI Tutor access logged successfully');
      } catch (error) {
        console.error('❌ Failed to log AI Tutor access:', error);
      }
    };

    logAITutorAccess();
  }, [user?.id]);

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="mb-8 text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Brain className="h-10 w-10 text-primary" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            AI Programming Companion
          </h1>
        </div>
        
        <p className="text-lg text-muted-foreground">
          Your Custom GPT powered by 746 course modules + 16,667 interview questions
        </p>
        
        {/* Feature Badges */}
        <div className="flex gap-2 justify-center flex-wrap mt-4">
          <Badge variant="secondary" className="gap-1">
            <Briefcase className="h-3 w-3" />
            16,667 Interview Q&A
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <GraduationCap className="h-3 w-3" />
            746 Learning Modules
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <Sparkles className="h-3 w-3" />
            Expert Frameworks & Tips
          </Badge>
        </div>

        {/* Quick Start Suggestions */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm font-medium text-foreground mb-2">Try asking:</p>
          <div className="flex flex-wrap gap-2 justify-center text-xs">
            <Badge variant="outline" className="cursor-pointer hover:bg-accent">
              "Give me a React interview question"
            </Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent">
              "Explain React hooks with examples"
            </Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent">
              "Prepare me for a fullstack interview"
            </Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent">
              "What are common SQL mistakes?"
            </Badge>
          </div>
        </div>
      </div>

      <ChatInterface />
    </div>
  );
};

const AITutor = () => {
  return (
    <ErrorBoundary 
      fallback={
        <Card className="max-w-md mx-auto mt-8">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <h2 className="text-lg font-semibold">AI Tutor Error</h2>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              The AI Tutor couldn't load properly. Please try refreshing the page.
            </p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      }
    >
      <AITutorContent />
    </ErrorBoundary>
  );
};

export default AITutor;
